/**
 * O QUE O SISTEMA TEM HOJE — fato, nao proposta.
 *
 * Duas listas, e as duas sao conferiveis abrindo a aplicacao:
 *
 *  - `MODULOS_DO_SISTEMA` e o mapa da solucao que a navegacao lateral desenha.
 *    Ela era uma constante privada de `Navegacao.tsx`; virou este modulo quando
 *    a tela de login passou a precisar da MESMA lista para dizer o que cada
 *    perfil ve. Duas copias divergiriam na primeira vez que um modulo nascesse,
 *    e a divergencia apareceria como "o login promete um modulo que o menu nao
 *    tem".
 *
 *  - `ACOES_DO_SPRINT` e o inventario do que da para LER e ESCREVER agora.
 *    Cada linha corresponde a algo que existe na tela — inclusive os botoes que
 *    estao la e estao DESLIGADOS. Botao desligado nao e acao inventada: ele
 *    esta na tela, a pessoa o ve, e a conversa sobre permissao precisa dele.
 *    O campo `situacao` separa os dois casos sem deixar duvida.
 *
 * NADA AQUI E SUPOSICAO SOBRE O FUTURO. Modulo que nao existe nao ganha acao.
 *
 * ## O que esta lista deixou de mentir nesta rodada
 *
 * Ela envelheceu junto com o sprint: dizia que cadastrar equipamento estava
 * DESLIGADO (a tela existe e grava), que o lancamento de combustivel "nao
 * chega a banco nenhum" (ele chega, e debita o tanque na mesma transacao) e
 * que dava para ver custo por hora (a coluna saiu, porque o numero e do modulo
 * 11 e nao existe). Um inventario que descreve a versao anterior e pior do que
 * nenhum: ele e lido como se fosse a versao atual.
 */

/** Os unicos caminhos que a aplicacao tem hoje. */
export type CaminhoConstruido =
  | '/equipamentos'
  | '/equipamentos/novo'
  | '/equipamentos/catalogo'
  | '/equipamentos/identificacao'
  | '/equipamentos/renovacao'
  | '/combustivel'
  | '/combustivel/lancamento'
  | '/combustivel/lancamento/completo'
  | '/combustivel/tanques'
  | '/combustivel/ordens'
  | '/combustivel/consumo'
  | '/combustivel/cobrancas'
  | '/combustivel/folhas'
  | '/combustivel/cadastros'
  | '/operacao'
  | '/operacao/cadastros'
  | '/operacao/folhas'
  | '/operacao/conciliacao'
  | '/operacao/importacoes'
  | '/operacao/telemetria'
  /* AS SEGUNDAS TELAS DOS TRES SUBMODULOS entraram no caminho construido em
     25/10/2026, quando o dono pediu o fim das abas internas: elas eram abas
     dentro da tela do submodulo e agora sao secoes da navegacao lateral, e o
     menu so navega para caminho que esta nesta uniao. */
  | '/operacao/telemetria/fila'
  | '/operacao/telemetria/conciliacao'
  | '/operacao/telemetria/integracoes'
  | '/manutencao'
  | '/manutencao/preditiva'
  | '/manutencao/preditiva/recorrencias'
  | '/manutencao/preditiva/previsoes'
  | '/manutencao/preditiva/propostas'
  | '/ordens'
  | '/inspecao'
  | '/inspecao/checklists'
  | '/pessoas'
  | '/estoque'
  | '/compras'
  | '/painel'
  | '/painel/gestor'
  | '/painel/ia'
  | '/painel/piloto'
  | '/financeiro'
  | '/orcamento'
  | '/investimentos'
  | '/custos'
  | '/receita'
  | '/tecnico'
  | '/cadastros'
  | '/cadastros/governanca'
  | '/cadastros/trilha'
  | '/cadastros/integracoes'
  | '/cadastros/integracoes/lotes'
  | '/cadastros/integracoes/titulos'
  | '/cadastros/integracoes/correspondencias'
  | '/cadastros/integracoes/divergencias';

export type IdModulo =
  | 'equipamentos'
  | 'combustivel'
  | 'manutencao'
  | 'ordens'
  | 'horimetro'
  | 'obras'
  | 'operadores'
  | 'pecas'
  | 'custos'
  | 'relatorios'
  | 'biblioteca';

export interface ModuloDoSistema {
  readonly id: IdModulo;
  readonly rotulo: string;
  /** Ausente enquanto o modulo nao existe. */
  readonly para?: CaminhoConstruido;
  /**
   * O ciclo em que o modulo entra, SO quando o repositorio registra isso em
   * algum lugar. Ausente nao quer dizer "sem previsao": quer dizer que a
   * previsao esta no guia de construcao e nao foi trazida para ca. A tela
   * escreve essa diferenca em vez de inventar um numero.
   */
  readonly ciclo?: string;
}

/** Na mesma ordem em que a navegacao lateral os mostra. */
export const MODULOS_DO_SISTEMA = [
  { id: 'equipamentos', rotulo: 'Equipamentos', para: '/equipamentos' },
  { id: 'combustivel', rotulo: 'Combustível', para: '/combustivel' },
  /* O MODULO 05 ENTROU AQUI em 22/09, pelo passo 5 da sequencia do guia:
     plano preventivo, revisao, publicacao e a fila de vencimentos. O icone ja
     era `wrench`, que e a entrada "manutencao" do vocabulario normativo. */
  { id: 'manutencao', rotulo: 'Manutenção', para: '/manutencao' },
  /* O MODULO 06 ENTROU AQUI em 22/09, pelo passo 6 da sequencia do guia. O
     icone ja era `clipboard-list`, a entrada "ordem de servico" do manual. */
  { id: 'ordens', rotulo: 'Ordens de serviço', para: '/ordens' },
  /* O MODULO 03 DO v6 ENTROU AQUI, pelo mesmo caminho do 01 logo abaixo.
     O mapa desta navegacao nasceu com "Horimetro", que era verdade enquanto so
     a leitura de medidor existia (fronteira minima da migracao 0007). O v6
     chama o modulo de "Operacao, horas e paradas", e o que a tela faz e maior
     que o horimetro: apontamento de turno, horas por natureza e causa de
     parada. O rotulo passa a dizer isso.

     O `id` continua `horimetro`, e pela mesma razao registrada em `obras`: e
     ele que a proposta de permissao ja usa, e trocar o id reescreveria a
     proposta do dono para caber num nome interno.

     O ICONE NAO MUDA: `clock` e a entrada "horimetro / tempo" do vocabulario
     normativo (p.14), e "operacao" nao tem entrada no manual. Criar uma e
     decisao de quem assina o manual, nao da tela. */
  { id: 'horimetro', rotulo: 'Operação', para: '/operacao' },
  /* O MODULO 01 DO MAPA FUNCIONAL v6 ENTROU AQUI, e nao como entrada nova.
     O mapa desta navegacao nasceu do guia de construcao, que fala em
     "Obras"; o v6 chama o mesmo assunto de "Cadastros e governanca", e o
     que a tela cadastra e maior do que obra: filial, cliente, fornecedor,
     centro de custo, obra e contrato, mais papeis e alcadas. O rotulo passa
     a dizer isso; o `id` continua `obras` porque e ele que a matriz de
     permissoes proposta ja usa, e trocar o id reescreveria a proposta do
     dono para caber num nome interno.

     O ICONE NAO MUDOU, e isso e regra, nao economia: `map-pin` e a entrada
     "obra / local" do vocabulario normativo (p.14), e "cadastro" nao tem
     entrada no manual. Criar uma e decisao de quem assina o manual, nao da
     tela — esta registrado em docs/PENDENCIAS.md. */
  { id: 'obras', rotulo: 'Cadastros', para: '/cadastros' },
  /* DEIXOU DE ESTAR DESLIGADO em 01/10, com o modulo 16. O rotulo mudou de
     "Operadores" para "Pessoas e documentos" porque a tela nao e o cadastro
     de quem opera — ela e a conferencia documental, de pessoa E de maquina. */
  { id: 'operadores', rotulo: 'Pessoas e documentos', para: '/pessoas' },
  /* O MODULO 07 ENTROU AQUI em 22/09, pelo passo 7 da sequencia do guia. O
     rotulo virou "Estoque" porque a tela e maior que peca: filtro, pneu,
     lubrificante, e o combustivel do tanque na mesma lista (decisao 13). O `id`
     continua `pecas` pelo mesmo motivo de `obras` e `horimetro`: e ele que a
     proposta de permissao usa. O icone continua `package`, a entrada
     "peca / estoque" do manual. */
  { id: 'pecas', rotulo: 'Estoque', para: '/estoque' },
  /* O MODULO 11 (custo direto) ENTROU AQUI em 22/09, com a decisao 3 do dono
     (o criterio de custeio como parametro). O icone ja era
     `circle-dollar-sign`, a entrada "custo" do manual. */
  { id: 'custos', rotulo: 'Custos', para: '/custos' },
  /* O PAINEL DE EXCECOES (passo 9, `DASH 01`) entrou no item que era
     "Relatorios", e a decisao e provisoria: o menu nasceu com relatorios, e o
     painel e o primeiro relatorio que o sistema tem — o icone `bar-chart-3` e a
     entrada "relatorio" do manual. Nao tem permissao propria: cada secao dele
     obedece a do modulo de origem. Registrado em docs/PENDENCIAS.md. */
  { id: 'relatorios', rotulo: 'Painel', para: '/painel' },
  /* O MODULO 12 TEM TELA DESDE 20/09 (`/tecnico`), e o item seguia sem
     caminho: o menu o desenhava desligado, com a nota de "nao construido",
     enquanto a biblioteca estava no ar. Achado em 24/09, ao montar o menu em
     sanfona. */
  { id: 'biblioteca', rotulo: 'Biblioteca técnica', para: '/tecnico' },
] as const satisfies readonly ModuloDoSistema[];

/**
 * Os modulos que EXISTEM, derivados da lista acima — nao escritos de novo.
 *
 * E este tipo que impede acao inventada: `AcaoDoSprint.modulo` so aceita um
 * modulo construido, entao uma acao de manutencao ou de ordens de servico para
 * de compilar em vez de virar promessa na tela de login. Quando um modulo
 * nascer, ele ganha `para` e passa a aceitar acoes sozinho; nenhuma segunda
 * lista precisa ser lembrada.
 */
export type IdModuloConstruido = Extract<
  (typeof MODULOS_DO_SISTEMA)[number],
  { readonly para: CaminhoConstruido }
>['id'];

export function moduloPorId(id: IdModulo): ModuloDoSistema {
  const achado = MODULOS_DO_SISTEMA.find((modulo) => modulo.id === id);
  if (!achado) {
    throw new Error(`Módulo "${id}" não está no mapa da solução.`);
  }
  return achado;
}

export function moduloEstaConstruido(modulo: ModuloDoSistema): boolean {
  return modulo.para !== undefined;
}

export type IdAcao =
  | 'frota.lista'
  | 'frota.detalhe'
  | 'frota.custo'
  | 'frota.exportacao'
  | 'frota.cadastro'
  | 'frota.exclusao'
  | 'combustivel.lista'
  | 'combustivel.lancamento'
  | 'cadastros.estrutura'
  | 'cadastros.governanca'
  | 'cadastros.trilha'
  | 'receita.medicoes'
  | 'receita.montar'
  | 'receita.aprovar'
  | 'receita.documento'
  | 'receita.margem'
  | 'compras.necessidade'
  | 'compras.requisicao'
  | 'compras.pedido'
  | 'compras.aprovacao'
  | 'compras.recebimento'
  | 'inspecao.fila'
  | 'inspecao.cobertura'
  | 'inspecao.checklist'
  | 'inspecao.fazer'
  | 'inspecao.encerrar'
  | 'inspecao.evidencia'
  | 'pessoas.impedimento'
  | 'pessoas.documento'
  | 'pessoas.aptidao'
  | 'pessoas.exigencia'
  | 'painel.excecoes'
  | 'gestor.executivo'
  | 'gestor.fila'
  | 'gestor.resultado';

export type TipoDeAcao = 'ler' | 'escrever';

/**
 * `funciona`       = a acao acontece de verdade nesta versao.
 * `desligada`      = o controle esta NA TELA e nao responde, com o motivo ao
 *                    lado dele.
 * `nao-desenhada`  = nao ha controle nenhum na tela para esta acao.
 *
 * O terceiro valor nasceu de um erro medido: "Exportar lista" estava marcada
 * `desligada`, e a definicao de `desligada` promete um botao que existe — e
 * nao ha botao de exportar em lugar nenhum de `src/`. Dizer "esta na tela e
 * desligado" sobre um controle inexistente manda quem le procurar o botao.
 *
 * Separar os tres importa para a conversa de permissao: conceder uma acao que
 * nao responde (ou que nao existe) nao muda nada hoje, e a tela diz qual dos
 * dois casos e.
 */
export type SituacaoDaAcao = 'funciona' | 'desligada' | 'nao-desenhada';

export interface AcaoDoSprint {
  readonly id: IdAcao;
  /** So modulo construido. Ver `IdModuloConstruido`, acima. */
  readonly modulo: IdModuloConstruido;
  readonly tipo: TipoDeAcao;
  readonly situacao: SituacaoDaAcao;
  /** Verbo + objeto, como o botao ou a tela escreve. */
  readonly rotulo: string;
  /** Frase completa: o que a acao entrega, ou por que nao responde. */
  readonly detalhe: string;
}

/**
 * O `as const satisfies` — e nao uma anotacao de tipo — existe pela mesma
 * conta de `MODULOS_DO_SISTEMA` e de `ALCANCE_PROPOSTO`: sem ele o tipo de
 * cada elemento seria `AcaoDoSprint`, e `ModuloDaAcao` (em `proposta.ts`) nao
 * teria como saber a que modulo cada acao pertence. O `satisfies` continua
 * cobrando a forma inteira, campo por campo.
 */
export const ACOES_DO_SPRINT = [
  {
    id: 'frota.lista',
    modulo: 'equipamentos',
    tipo: 'ler',
    situacao: 'funciona',
    rotulo: 'Ver a lista da frota',
    /* SEM "horímetro": a coluna saiu da tabela na mesma rodada em que saiu a
       de custo. As colunas de hoje sao codigo, equipamento, cliente, obra,
       status, configuracao e tanque. */
    detalhe: 'Código, equipamento, cliente, obra, status com o motivo escrito, configuração e tanque.',
  },
  {
    id: 'frota.detalhe',
    modulo: 'equipamentos',
    tipo: 'ler',
    situacao: 'funciona',
    rotulo: 'Abrir o detalhe do equipamento',
    /* As QUATRO ABAS que o painel tem hoje. A ficha abre com o que os
       modulos 03, 08 e 12 sabem da maquina, cada parte sob a permissao do
       proprio modulo. Operador ainda nao: o apontamento guarda, o prontuario
       nao mostra. */
    detalhe: 'Quatro abas: ficha com horímetro, horas e abastecimentos dos últimos 30 dias, preventivas, ordens de serviço e documentos técnicos; alocação com vigência; situação com o motivo escrito; e a linha do tempo do ativo.',
  },
  {
    id: 'frota.custo',
    modulo: 'equipamentos',
    tipo: 'ler',
    situacao: 'nao-desenhada',
    /* SAIU DA TELA quando a frota deixou de ler fixture. Custo por hora é
       apropriação (módulo 11) e não existe no modelo; a coluna só sabia
       mostrar vazio, e coluna vazia em quarenta linhas se lê como defeito, não
       como "ainda não temos isso". A ação continua no inventário porque a
       proposta a concede a três perfis, e sumir com ela esconderia a diferença
       entre "não recebe" e "não existe". */
    rotulo: 'Ver o custo por hora',
    detalhe:
      'A coluna saiu da tela: custo por hora é apropriação de custo, e não existe no modelo de dados.',
  },
  {
    id: 'frota.exportacao',
    modulo: 'equipamentos',
    tipo: 'ler',
    situacao: 'nao-desenhada',
    rotulo: 'Exportar lista',
    detalhe: 'O controle ainda não foi desenhado: não há botão de exportar em tela nenhuma. Entra junto com o relatório de frota.',
  },
  {
    id: 'frota.cadastro',
    modulo: 'equipamentos',
    tipo: 'escrever',
    situacao: 'funciona',
    rotulo: 'Cadastrar equipamento',
    detalhe:
      'Fabricante, modelo, configuração e a unidade, com o que ela herda da configuração. Grava no banco, com trilha.',
  },
  {
    id: 'frota.exclusao',
    modulo: 'equipamentos',
    tipo: 'escrever',
    situacao: 'funciona',
    /* O rótulo continua "Excluir" porque é o rótulo da PROPOSTA do dono, e o
       que a tela faz é desativar — ela diz isso na confirmação, com a
       consequência escrita. Apagar uma máquina apagaria a base de apropriação
       de todo custo já lançado nela. */
    rotulo: 'Excluir equipamento',
    detalhe:
      'A tela desativa em vez de apagar, e diz isso na confirmação: a máquina sai das listas de trabalho e o histórico dela fica inteiro.',
  },
  {
    id: 'combustivel.lista',
    modulo: 'combustivel',
    tipo: 'ler',
    situacao: 'funciona',
    rotulo: 'Ver os lançamentos de abastecimento',
    detalhe:
      'Litros, preço do litro por tanque e consumo por máquina, mais o saldo dos tanques, as ordens e a conciliação de cobrança.',
  },
  {
    id: 'combustivel.lancamento',
    modulo: 'combustivel',
    tipo: 'escrever',
    situacao: 'funciona',
    rotulo: 'Lançar abastecimento',
    detalhe:
      'Interno ou externo, com a leitura do medidor e a saída do tanque lançadas na mesma transação do banco.',
  },
  {
    id: 'cadastros.estrutura',
    modulo: 'obras',
    tipo: 'escrever',
    situacao: 'funciona',
    rotulo: 'Cadastrar a estrutura',
    detalhe:
      'Filial, cliente, fornecedor, centro de custo, obra e contrato de referência. Grava no banco, com trilha de auditoria.',
  },
  {
    id: 'cadastros.governanca',
    modulo: 'obras',
    tipo: 'escrever',
    situacao: 'funciona',
    rotulo: 'Definir papéis e alçadas',
    detalhe:
      'Papel, permissão e quem aprova compra, ajuste, O.S. e liberação. Grava no banco, e o servidor confere a permissão do papel em cada pedido.',
  },
  {
    id: 'cadastros.trilha',
    modulo: 'obras',
    tipo: 'ler',
    situacao: 'funciona',
    rotulo: 'Ver a trilha de auditoria',
    detalhe: 'Autor, momento, motivo e como a linha estava antes de cada alteração.',
  },
  {
    id: 'receita.medicoes',
    modulo: 'custos',
    tipo: 'ler',
    situacao: 'funciona',
    rotulo: 'Ver as medições do contrato',
    detalhe: 'Competência, contrato e cliente com o estado do faturamento na segunda linha, situação escrita, equipamentos, horas reconhecidas e o valor da medição. O rodapé soma só o aprovado.',
  },
  {
    id: 'receita.montar',
    modulo: 'custos',
    tipo: 'escrever',
    situacao: 'funciona',
    rotulo: 'Montar a medição de um contrato',
    /* MONTAR NAO E APROVAR, e o inventario escreve a diferenca porque ela e a
       pergunta que quem apresenta o sistema ouve primeiro. */
    detalhe: 'Monta a proposta a partir da alocação do período, das horas que a conciliação reconheceu e da regra vigente, tirando um instantâneo dela. A medição nasce em aberto e não entra na receita até ser aprovada.',
  },
  {
    id: 'receita.aprovar',
    modulo: 'custos',
    tipo: 'escrever',
    situacao: 'funciona',
    rotulo: 'Aprovar ou cancelar a medição',
    detalhe: 'O aceite é o que transforma hora reconhecida em receita, e é licença separada de montar: quem monta propõe, outra pessoa aceita. Cancelar exige motivo com pelo menos 15 caracteres, e libera a competência para uma medição nova.',
  },
  {
    id: 'receita.documento',
    modulo: 'custos',
    tipo: 'escrever',
    situacao: 'funciona',
    rotulo: 'Emitir o documento e registrar o recebimento',
    detalhe: 'O documento aponta para a medição aprovada e não carrega valor próprio; o recebimento é uma data e um valor nele. Nenhum dos dois é receita nova — somá-los triplicaria o faturamento, que é o risco que o documento v6 escreve para este módulo.',
  },
  {
    id: 'receita.margem',
    modulo: 'custos',
    tipo: 'ler',
    situacao: 'funciona',
    rotulo: 'Ver a margem por contrato',
    /* 0096: a margem passou a ter o custo TOTAL (direto, depreciacao e
       indireto rateado, com a maquina de varios contratos dividida pela
       hora), pela mesma conta da Visao do gestor. A coluna existe e diz
       "parcial" enquanto algo ficar fora da competencia. */
    detalhe: 'A margem por contrato sai da receita aprovada menos o custo total — direto, depreciação e custo indireto rateado —, pela mesma conta da Visão do gestor. Ela só se chama margem quando nada ficou fora na competência; senão a célula diz "parcial", e a frase do servidor diz o que faltou.',
  },
  {
    id: 'compras.necessidade',
    modulo: 'pecas',
    tipo: 'ler',
    situacao: 'funciona',
    rotulo: 'Ver o que falta comprar, descontando o que já foi pedido',
    /* A SUBTRACAO E O MODULO. A tela mostra as quatro parcelas na mesma linha
       — demanda, disponivel, em pedido, comprar — porque "comprar 0" com
       demanda 10 se leria como defeito sem as duas do meio. */
    detalhe: 'Comprar = demanda − disponível − pedido já aberto, com as quatro parcelas na linha. A demanda vem das preventivas que vencem logo e do mínimo de estoque, e a mesma peça pedida pelas duas conta uma vez. Pedido em rascunho não desconta.',
  },
  {
    id: 'compras.requisicao',
    modulo: 'pecas',
    tipo: 'escrever',
    situacao: 'funciona',
    rotulo: 'Registrar uma necessidade com a origem escrita',
    detalhe: 'Para o que não vem do mínimo nem da preventiva — a peça que alguém viu gastando. A origem fica escrita porque "por que compramos isso" não tem resposta três meses depois sem ela, e a origem "ordem de serviço" exige a O.S.: origem que não se pode conferir não é origem. O campo da O.S. só aparece quando é essa a origem.',
  },
  {
    id: 'compras.pedido',
    modulo: 'pecas',
    tipo: 'escrever',
    situacao: 'funciona',
    rotulo: 'Montar e emitir o pedido ao fornecedor',
    detalhe: 'O pedido nasce em rascunho, com fornecedor e prazo em dias. Emitir exige o pedido aprovado — e essa é regra do banco, não da tela. Depois de aprovado, o item não muda mais: trocar a linha passaria por cima da aprovação.',
  },
  {
    id: 'compras.aprovacao',
    modulo: 'pecas',
    tipo: 'escrever',
    situacao: 'funciona',
    rotulo: 'Aprovar ou cancelar o pedido',
    detalhe: 'É o único portão de gasto desta fase, e é licença separada de montar o pedido: quem monta propõe, o aprovador decide. Pedido sem item não se aprova. Cancelar exige motivo com pelo menos 15 caracteres, e devolve a necessidade para a lista de compras.',
  },
  {
    id: 'compras.recebimento',
    modulo: 'pecas',
    tipo: 'escrever',
    situacao: 'funciona',
    rotulo: 'Registrar o que chegou, dando entrada no estoque',
    detalhe: 'O recebimento é parcial por natureza e grava a entrada nos movimentos do estoque na mesma transação — não num saldo paralelo. Receber mais do que foi pedido é recusado pelo banco, e o pedido só fica concluído quando nenhum item tem falta. O custo real do item entra por aqui, e não pelo preço estimado do pedido.',
  },
  {
    id: 'inspecao.fila',
    modulo: 'ordens',
    tipo: 'ler',
    situacao: 'funciona',
    rotulo: 'Ver os achados de campo que ninguém tratou',
    /* A TELA ABRE AQUI, e nao na lista de formularios preenchidos: uma tela
       que mostra o trabalho feito em vez do problema pendente e exatamente a
       formalidade que o v6 descreve como risco. */
    detalhe: 'A fila por criticidade, com os dias em aberto, a O.S. quando existe e quem pediu bloqueio. A crítica vem primeiro, e o número de dias fica vermelho quando passa de três.',
  },
  {
    id: 'inspecao.cobertura',
    modulo: 'ordens',
    tipo: 'ler',
    situacao: 'funciona',
    rotulo: 'Ver quem ainda não foi verificado',
    detalhe: 'Por máquina e checklist publicado, quando foi a última inspeção concluída. A máquina que nunca foi inspecionada aparece escrito "Nunca inspecionada", e não como travessão: um travessão se leria como falta de dado, e o risco é máquina usada sem verificação registrada.',
  },
  {
    id: 'inspecao.checklist',
    modulo: 'ordens',
    tipo: 'escrever',
    situacao: 'funciona',
    rotulo: 'Montar e publicar o checklist de um modelo',
    detalhe: 'Em Ordens de serviço › Montar checklists, numa página inteira porque a lista de itens cresce. Os três passos na ordem que o banco impõe: criar em rascunho, acrescentar os itens, publicar com a nota da revisão técnica. A janela de item não fecha a cada linha e a ordem avança sozinha — montar trinta itens reabrindo a janela trinta vezes é o atrito que faz a lista ficar pela metade. Publicar arquiva o publicado anterior do mesmo alvo e rotina, e a tela diz isso antes de confirmar.',
  },
  {
    id: 'inspecao.fazer',
    modulo: 'ordens',
    tipo: 'escrever',
    situacao: 'funciona',
    rotulo: 'Abrir e concluir a inspeção de uma máquina',
    /* O PONTO DO MODULO: concluir nao encerra achado, e a tela diz isso. */
    detalhe: 'Só checklist publicado vira inspeção. Marcar um item como não conforme abre a ocorrência na mesma transação — pelo banco, não pela tela. Concluir exige todo item respondido e NÃO encerra nenhuma ocorrência: a tela escreve quantas continuam abertas e quantas são críticas.',
  },
  {
    id: 'inspecao.encerrar',
    modulo: 'ordens',
    tipo: 'escrever',
    situacao: 'funciona',
    rotulo: 'Encerrar a ocorrência, ou pedir o bloqueio da máquina',
    detalhe: 'Encerrar é licença separada de inspecionar: quem relata não fecha o próprio achado. O desfecho é um dos três — virou O.S., resolvida no local, não procede —, e a crítica sem O.S. exige pelo menos 20 caracteres dizendo o que foi feito. Pedir bloqueio sinaliza e não para a máquina: parar é registrado em Operação, com a causa escrita.',
  },
  {
    id: 'inspecao.evidencia',
    modulo: 'ordens',
    tipo: 'escrever',
    situacao: 'funciona',
    rotulo: 'Anexar foto e observação à ocorrência',
    detalhe: 'Em Mais ações › Fotos e observações, na fila de ocorrências. O tipo é conferido pelo conteúdo do arquivo e não pelo nome; o hash e a chave são do servidor, e a leitura é por URL que vale cinco minutos. Várias por ocorrência, e nenhuma se edita nem se apaga: evidência é prova do que o campo viu, e corrigir é anexar outra com a descrição dizendo o que mudou. Achado já encerrado não recebe evidência — mexer no que alguém fechou com justificativa escrita não é corrigir o registro.',
  },
  {
    id: 'pessoas.impedimento',
    modulo: 'operadores',
    tipo: 'ler',
    situacao: 'funciona',
    rotulo: 'Ver o que impede a operação hoje',
    /* O NUMERO DO MODULO. Ele cruza tres coisas que antes nao se falavam, e o
       inventario diz isso porque e a pergunta que quem apresenta ouve. */
    detalhe: 'Por obra alocada, o documento que o contrato ou a obra exige e está ausente, vencido ou sem validade — de máquina e de quem está autorizado a operá-la. Os três motivos aparecem separados, com o que fazer em cada um: ausente é buscar, vencido é renovar, sem data é corrigir o cadastro.',
  },
  {
    id: 'pessoas.documento',
    modulo: 'operadores',
    tipo: 'ler',
    situacao: 'funciona',
    rotulo: 'Ver e cadastrar documentos e vencimentos',
    detalhe: 'Documento de pessoa e de máquina na mesma lista, com o estado calculado pelo aviso em dias de cada tipo. Cadastrar e renovar pela tela: renovar grava a data nova no mesmo documento, porque dois válidos do mesmo tipo fariam a conferência escolher um e esconder qual. O campo do dono segue o tipo escolhido — mostrar os dois convidaria a preencher ambos, que o banco recusa. Ver documento de pessoa é licença separada: CNH, ASO e exame não são assunto de quem cuida de máquina.',
  },
  {
    id: 'pessoas.aptidao',
    modulo: 'operadores',
    tipo: 'escrever',
    situacao: 'funciona',
    rotulo: 'Autorizar alguém a operar, e encerrar a autorização',
    detalhe: 'A autorização é por modelo ou configuração, nunca por máquina — por patrimônio ela envelheceria a cada equipamento novo. Encerrar fecha a vigência e não apaga a linha: a história de quem estava autorizado no dia do acidente é o que alguém vai perguntar.',
  },
  {
    id: 'pessoas.exigencia',
    modulo: 'operadores',
    tipo: 'escrever',
    situacao: 'funciona',
    rotulo: 'Cadastrar a exigência do cliente, e marcá-la como validada',
    detalhe: 'A lista de documentos que cada contrato ou obra exige, com quem a validou e quando. Sem isso, "o cliente exige" é boato — e um impedimento apoiado em boato ensina a ignorar a lista. Cadastrar e marcar como validada, as duas pela tela; a exigência é do contrato OU de uma obra dele, e a tela não deixa preencher os dois.',
  },
  {
    id: 'painel.excecoes',
    modulo: 'relatorios',
    tipo: 'ler',
    situacao: 'funciona',
    rotulo: 'Ver o painel de exceções',
    detalhe: 'Indisponibilidade, O.S. abertas há mais tempo, preventivas vencidas, estoque abaixo do mínimo, o que vai faltar para as preventivas, divergências de combustível e registros faltantes. Cada seção lê o módulo de origem, sem conta nova, e diz quando foi o último registro que a sustenta — o v6 escreve o risco de "tratar um painel desatualizado como visão em tempo real".',
  },
  {
    id: 'gestor.executivo',
    modulo: 'relatorios',
    tipo: 'ler',
    situacao: 'funciona',
    rotulo: 'Ver o painel executivo do período',
    detalhe: 'Custo direto, custo por hora pelos DOIS denominadores (hora de motor e hora de produção, com o denominador escrito ao lado de cada um), hora parada por causa evitável e as horas que a conciliação ainda não explicou. O custo das máquinas sem hora apontada fica fora da divisão e volta à parte: sem isso, "R$ 0,00/h" ao lado de milhares de reais se leria como erro de conta.',
  },
  {
    id: 'gestor.fila',
    modulo: 'relatorios',
    tipo: 'ler',
    situacao: 'funciona',
    rotulo: 'Ver a fila de prioridades por máquina',
    /* A REGRA SAI NA TELA, com os pesos que vieram do servidor: numero de
       prioridade sem explicacao e so outra urgencia aparente, que e o risco
       que a ferramenta 4 do v6 existe para evitar. */
    detalhe: 'Ordena as máquinas por impacto, com o porquê de cada ponto escrito na linha e os pesos da regra visíveis na tela. Sinal de módulo que a pessoa não tem permissão de ler fica FORA da conta, e a tela diz quais — uma fila que ignorou metade dos sinais sem avisar ordena errado com cara de certa.',
  },
  {
    id: 'gestor.resultado',
    modulo: 'relatorios',
    tipo: 'ler',
    situacao: 'funciona',
    rotulo: 'Ver custo, receita e resultado por contrato',
    /* A ferramenta 3, que entrou em 01/10 quando a receita passou a existir.
       O que o inventario precisa dizer dela e o que ela NAO faz. */
    detalhe: 'Mensal, porque a medição é mensal — cruzar a receita de setembro com o custo de meio setembro a meio outubro daria um resultado de nenhum período. Nada se chama lucro, e as premissas aparecem antes da tabela, não como nota de rodapé. O custo é o total — direto, depreciação e indireto rateado —, e a máquina que serviu mais de um contrato se divide pelas horas reconhecidas (ou apontadas) de cada um; a que não tem hora fica à parte, com o que resolve. O resultado só se diz completo quando nada ficou fora do mês.',
  },
] as const satisfies readonly AcaoDoSprint[];

export function acaoPorId(id: IdAcao): AcaoDoSprint {
  const achada = ACOES_DO_SPRINT.find((acao) => acao.id === id);
  if (!achada) {
    throw new Error(`Ação "${id}" não está no inventário do Sprint 1.`);
  }
  return achada;
}
